import toast from "react-hot-toast";
import { clearFilterState, clearGhostSession } from "./session";

// A page can fire several independent requests at once (city options, owner
// list, stats, household data, etc.), especially right after reconnecting
// when multiple mount effects all re-fire together. If HubSpot/the backend
// rate-limits (429) during that burst, each call's own catch block showing
// its own toast stacks up as several near-identical "Request failed with
// status code 429" popups. Route 429s (and other raw axios failures with no
// server-provided message) through this shared helper instead: it shows one
// friendly, de-duplicated toast (same `id` replaces any still-visible one)
// rather than a raw axios message repeated once per failed request.
export const showApiError = (error, fallbackMessage = "Something went wrong") => {
  const serverMessage = error?.response?.data?.message;
  if (serverMessage) {
    toast.error(serverMessage);
    return;
  }
  if (error?.response?.status === 429) {
    toast.error("Too many requests right now - please wait a moment and try again.", {
      id: "rate-limited",
    });
    return;
  }
  if (!error?.response) {
    // Network-level failure (no response at all) - already handled by the
    // offline-cache fallback logic where it matters; avoid also surfacing
    // axios's raw "Network Error" text as a toast here.
    console.error(fallbackMessage, error);
    return;
  }
  toast.error(fallbackMessage);
};

// A fast (cached/local) response resolves before the skeleton has rendered
// even one visible frame, so the loading state flashes on and off instantly -
// reads as a flicker rather than a loading indicator. Pads any fetch to a
// minimum wall-clock duration so the skeleton is always shown long enough
// to register.
export const withMinDuration = async (promise, minMs = 350) => {
  const start = Date.now();
  try {
    return await promise;
  } finally {
    const elapsed = Date.now() - start;
    if (elapsed < minMs) {
      await new Promise((resolve) => setTimeout(resolve, minMs - elapsed));
    }
  }
};

export const getLocationRadiusCoordinates = (lat, lon, radius) => {
  const milesPerDegreeLat = 69;
  const milesPerDegreeLon = 69 * Math.cos(lat * (Math.PI / 180));

  const deltaLat = radius / milesPerDegreeLat;
  const deltaLon = radius / milesPerDegreeLon;

  return {
    minLat: lat - deltaLat,
    maxLat: lat + deltaLat,
    minLon: lon - deltaLon,
    maxLon: lon + deltaLon,
  };
};
export const storeLocalData = (name, data) => localStorage.setItem(name, data);
export const getLocalData = (name) => localStorage.getItem(name);
export const removeLocalData = (name) => localStorage.removeItem(name);

export const storeObjectData = (name, data) =>
  localStorage.setItem(name, JSON.stringify(data));
export const getObjectData = (name) => JSON.parse(localStorage.getItem(name));

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      }
    );
  });
};

export const clearStorage = () => {
  // app_build_version deliberately NOT cleared here - it's a global build
  // version shared by every tenant, not per-user/tenant data, so clearing it
  // on logout would only cause an already-dismissed update banner to
  // reappear for the next login on the same device. See
  // docs/implementation-plans/2026-09-29-app-version-update-banner.md.
  const keysToRemove = [
    "token",
    "isVerify",
    "ownerId",
    "urserMail",
    "userRole",
    "cityname",
    "currentLocation",
    "version",
    "userRoles",
    "isSuperAdmin",
    "currentTenantId",
  ];
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  clearFilterState();
  clearGhostSession();
  sessionStorage.clear();
  // Deliberately does NOT touch the Cache Storage API (service worker cache)
  // or IndexedDB - this used to call caches.delete() on every cache here,
  // which wiped out the offline SPA-shell cache (sw.js) that a hard page
  // reload while offline depends on, and had nothing to do with logging the
  // user out. Clicking this "refresh" button while offline would clear the
  // auth token AND destroy the only cached shell available to load "/" from,
  // leaving the app unable to load at all until back online.
  window.location.href = "/";
};

export const refreshAppData = async () => {
  ["tenantTheme", "cities"].forEach((key) => localStorage.removeItem(key));
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.error("Clearing caches before refresh failed:", error);
  }
  window.location.reload();
};

export const reloadIfCurrentTenant = (tenantId) => {
  if (tenantId == null || Number(tenantId) !== Number(localStorage.getItem("currentTenantId"))) return;
  setTimeout(() => window.location.reload(), 800);
};

const currentTime = new Date();

export const formattedCurrent = currentTime
  .toLocaleString("sv-SE")
  .replace(" ", "T");

export const getCityValue = (city) => {
  const cityValues = city.map((cityData) => cityData.value);
  return cityValues;
};

export function parseDate(input) {
  let year, month, day;

  if (input instanceof Date) {
    year = input.getFullYear();
    month = input.getMonth() + 1; // 0-based
    day = input.getDate();
  }
  else if (typeof input === "string") {
    const parts = input.split("T")[0].split("-");
    year = Number(parts[0]);
    month = Number(parts[1]);
    day = Number(parts[2]);
  } else {
    throw new Error("Invalid date input");
  }
  const utcMidnight = Date.UTC(year, month - 1, day, 0, 0, 0);
  const milliseconds = utcMidnight;
  return milliseconds.toString()
}
export function parseDateFromMilliseconds(milliseconds) {
  if (!milliseconds) return null;

  let utcDate;

  if (typeof milliseconds === "string" && milliseconds.includes("-")) {
    const parts = milliseconds.split("T")[0].split("-");
    const year = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    return new Date(year, month, day);
  } else {
    const ms = typeof milliseconds === "string" ? Number(milliseconds) : milliseconds;
    utcDate = new Date(ms);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();
    return new Date(year, month, day);
  }
}
